import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { FaSave, FaAngleLeft } from 'react-icons/fa';
import NoAccess from '../../../Routes/noAccess';
import api from '../../services/saleVoucherRAApi';
import { getSelectedFinancialYear } from '../leaseGeneralInvoiceReceipt/formUtils';
import HeaderPanel from './HeaderPanel';
import PartyPanel from './PartyPanel';
import LinesGrid from './LinesGrid';
import { LEAVE_MESSAGE, SALE_VOUCHER_BASE, readPermission } from './saleVoucherConstants';
import {
    applyCopyCostCentres, clearChildCostCentres, emptyLine, fmt3, gridTotals, initialForm, mapDetailToForm, recomputeGrid,
    toSavePayload, validateVoucher,
} from './saleVoucherHelpers';

// New vouchers default to the Sale type: an exact "Sale" first, then the first type starting with it.
const defaultVoucherType = (types) => {
    const norm = (t) => `${t.text ?? t.value}`.trim().toLowerCase();
    return types.find((t) => norm(t) === 'sale') || types.find((t) => norm(t).startsWith('sale'));
};

/**
 * One screen for create, edit and view. Edit loads the voucher first and is refused when canEdit is false;
 * view renders the same panels read-only.
 * @param {{ mode: 'create'|'edit'|'view', voucherNo?: string, financialYear?: string }} props
 */
const SaleVoucherRAForm = ({ mode = 'create', voucherNo, financialYear }) => {
    const navigate = useNavigate();
    const isCreate = mode === 'create';
    const isEdit = mode === 'edit';
    const [permissions] = useState(() => readPermission());

    const [loadState, setLoadState] = useState('loading'); // loading | ready | error
    const [loadError, setLoadError] = useState('');
    const [lookups, setLookups] = useState({ voucherTypes: [], accounts: [], settings: null, taxCodes: [], currencies: [] });
    const [header, setHeader] = useState(null);
    const [form, setForm] = useState(() => initialForm());
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState(null);
    const [conflict, setConflict] = useState(false);

    const [partyOptions, setPartyOptions] = useState([]);
    const [partyLoading, setPartyLoading] = useState(false);
    const [partyWarning, setPartyWarning] = useState('');
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailMessage, setDetailMessage] = useState('');

    // Latest values for async handlers, which would otherwise read stale closures.
    const taxCodesRef = useRef([]);
    taxCodesRef.current = lookups.taxCodes;
    const currenciesRef = useRef([]);
    currenciesRef.current = lookups.currencies;
    const formRef = useRef(form);
    formRef.current = form;
    const taxTouched = useRef(false);
    const autoTax = useRef(false);
    const firstPartyLoad = useRef(true);
    const detailRequest = useRef(0);

    const locked = mode === 'view' || (isEdit && header && !header.canEdit);
    const fy = header?.financialYear || financialYear || getSelectedFinancialYear();
    const totals = useMemo(() => gridTotals(form), [form]);
    const canSave = !locked && !saving && totals.count > 0 && totals.difference === 0 && Number(form.amount) > 0;

    /* ---------------- form state helpers ---------------- */

    // Every change goes through here so the grid always reflects the tax codes, rate, date and inclusive flag.
    const updateWith = useCallback((fn, { silent = false } = {}) => {
        setForm((prev) => {
            let next = fn(prev);
            if (next.copyCC) next = applyCopyCostCentres(next);
            return recomputeGrid(next, taxCodesRef.current);
        });
        if (!silent) setDirty(true);
    }, []);
    const update = useCallback((patch, flags) => {
        if (patch.taxCode !== undefined && flags?.taxTouched) { taxTouched.current = true; autoTax.current = false; }
        updateWith((prev) => ({ ...prev, ...patch }));
    }, [updateWith]);

    /* ---------------- initial load ---------------- */

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [dd, settings, taxCodes, currencies] = await Promise.all([
                api.getDropdowns(['VoucherTypes', 'Accounts']), api.getSettings(), api.getTaxCodes(), api.getCurrencies(),
            ]);
            if (cancelled) return;
            const failed = [dd, settings, taxCodes, currencies].find((r) => !r.ok && r.status !== 204);
            if (failed) { setLoadError(failed.message); setLoadState('error'); return; }
            const loaded = {
                voucherTypes: dd.dropdowns?.VoucherTypes || [],
                accounts: dd.dropdowns?.Accounts || [],
                settings: settings.result,
                taxCodes: taxCodes.result || [],
                currencies: currencies.result || [],
            };
            taxCodesRef.current = loaded.taxCodes;
            setLookups(loaded);

            if (isCreate) {
                const def = loaded.currencies.find((c) => c.isDefault) || loaded.currencies[0];
                const saleType = defaultVoucherType(loaded.voucherTypes);
                setForm((prev) => ({
                    ...prev,
                    voucherType: saleType?.value || prev.voucherType,
                    ...(def ? { currency: def.code, conversionRate: def.conversionRate } : {}),
                }));
                setLoadState('ready');
                return;
            }
            const res = await api.getVoucherByNo(voucherNo, financialYear);
            if (cancelled) return;
            if (!res.ok) { setLoadError(res.message); setLoadState('error'); return; }
            taxTouched.current = true; // never overwrite a saved header tax code
            setHeader(res.result.header);
            setForm(recomputeGrid(mapDetailToForm(res.result), loaded.taxCodes));
            setLoadState('ready');
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, voucherNo, financialYear]);

    /* ---------------- party list (narrowed by property / unit) ---------------- */

    useEffect(() => {
        if (loadState !== 'ready') return undefined;
        let cancelled = false;
        (async () => {
            setPartyLoading(true);
            const res = await api.getPartyCustomers({ partyType: form.partyType, useCoa: form.useCoa, costCenter1: form.cc1, costCenter2: form.cc2 });
            if (cancelled) return;
            setPartyLoading(false);
            const options = (res.ok ? res.result || [] : []).map((p) => ({ value: p.customerNo, label: `${p.customerNo} — ${p.customerName}` }));
            if (!res.ok && res.status !== 204) toast.error(res.message);
            setPartyOptions(options);

            const current = formRef.current.customer;
            const initial = firstPartyLoad.current;
            firstPartyLoad.current = false;
            if (initial && !isCreate) return; // a saved voucher keeps its debit party even if the list is narrower
            const missing = current && !options.some((o) => o.value === current.value);
            if (missing) {
                setPartyWarning('The selected debit account is not available for this property/unit.');
                clearParty();
            } else {
                setPartyWarning('');
            }

            // Tenant / Landlord / Freehold with a property and unit chosen: fill the debit account with the match.
            const f = formRef.current;
            if (isCreate && (!current || missing) && f.partyType !== 'All' && !f.useCoa && f.cc1 && f.cc2) {
                if (options.length) handleCustomer(options[0]);
                else setPartyWarning(`No ${f.partyType} found for this unit.`);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadState, form.partyType, form.useCoa, form.cc1, form.cc2]);

    /* ---------------- debit party detail ---------------- */

    const loadDetail = useCallback(async (customerNo) => {
        const ticket = ++detailRequest.current;
        setDetail(null);
        setDetailMessage('');
        if (!customerNo) return;
        setDetailLoading(true);
        // Landlord / freehold codes are ledger codes like PM-43932; their customer record is keyed by the digits only.
        const numeric = /^[A-Za-z]+-/.test(customerNo) ? customerNo.replace(/\D/g, '') : '';
        let res = await api.getCustomerDetail({ customerNo: numeric || customerNo, financialYear: fy });
        if (!res.ok && numeric) res = await api.getCustomerDetail({ customerNo, financialYear: fy });
        if (ticket !== detailRequest.current) return;
        setDetailLoading(false);
        if (!res.ok) {
            setDetailMessage(res.status === 404 ? 'Customer not found.' : res.message);
            return;
        }
        setDetail(res.result);
        if (res.result?.taxCode && !taxTouched.current) {
            autoTax.current = true;
            updateWith((prev) => ({ ...prev, taxCode: res.result.taxCode }), { silent: true });
        }
    }, [fy, updateWith]);

    // View and edit show the saved party's panel once the voucher is loaded.
    useEffect(() => {
        if (loadState === 'ready' && !isCreate && form.customer?.value) loadDetail(form.customer.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadState]);

    const clearParty = () => {
        updateWith((prev) => ({ ...prev, customer: null }));
        detailRequest.current += 1;
        setDetail(null);
        setDetailMessage('');
        setDetailLoading(false);
    };

    // Changing the party kind or the Chart of Accounts switch starts the debit selection over.
    const resetPartySelection = (patch) => {
        setPartyWarning('');
        if (autoTax.current) { autoTax.current = false; patch = { ...patch, taxCode: '0' }; }
        updateWith((prev) => ({ ...prev, ...patch, customer: null }));
        detailRequest.current += 1;
        setDetail(null);
        setDetailMessage('');
        setDetailLoading(false);
    };

    const handleCustomer = (option) => {
        setPartyWarning('');
        update({ customer: option });
        loadDetail(option?.value);
    };

    /* ---------------- header handlers ---------------- */

    const handleCurrency = (code) => {
        const currency = currenciesRef.current.find((c) => c.code === code);
        update({ currency: code, conversionRate: currency ? currency.conversionRate : formRef.current.conversionRate });
    };
    const handleCostCentre = (level, value) =>
        updateWith((prev) => ({ ...clearChildCostCentres(prev, level), [`cc${level}`]: value }));
    const handleCopyCC = (checked) => update({ copyCC: checked });
    const handleSetFromLines = () => update({ amount: totals.linesTotal });
    const handleApplyTax = () => updateWith((prev) => ({ ...prev, lines: prev.lines.map((l) => ({ ...l, taxCode: prev.taxCode || '0' })) }));

    /* ---------------- grid handlers ---------------- */

    // Stable references so the memoised grid rows are not re-rendered by unrelated header changes.
    const handleLine = useCallback((index, patch) =>
        updateWith((prev) => ({ ...prev, lines: prev.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) })), [updateWith]);
    const handleAdd = () =>
        updateWith((prev) => ({
            ...prev,
            lines: [...prev.lines, emptyLine({ taxCode: prev.taxCode, cc1: prev.cc1, cc2: prev.cc2, cc3: prev.cc3, cc4: prev.cc4 })],
        }));
    const handleRemove = useCallback((index) =>
        updateWith((prev) => {
            const lines = prev.lines.filter((_, i) => i !== index);
            return { ...prev, lines: lines.length ? lines : [emptyLine({ taxCode: prev.taxCode })] };
        }), [updateWith]);

    /* ---------------- leaving the page ---------------- */

    const hasUnsaved = dirty && !saving && !locked;
    useEffect(() => {
        if (!hasUnsaved) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        // In-app links (the sidebar) never trigger beforeunload, so intercept them in the capture phase.
        const onClick = (e) => {
            const anchor = e.target.closest?.('a[href]');
            if (!anchor || anchor.target === '_blank' || !anchor.getAttribute('href').startsWith('/')) return;
            if (!window.confirm(LEAVE_MESSAGE)) { e.preventDefault(); e.stopPropagation(); }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, [hasUnsaved]);

    const handleCancel = () => {
        if (hasUnsaved && !window.confirm(LEAVE_MESSAGE)) return;
        navigate(SALE_VOUCHER_BASE);
    };

    /* ---------------- save ---------------- */

    const showMessages = (messages) => setBanner(
        <ul className="mb-0 ps-3">{messages.map((m) => <li key={m}>{m}</li>)}</ul>,
    );

    const handleSave = async () => {
        if (!canSave) return;
        const messages = validateVoucher(form, mode, lookups.settings, header?.amountReceived);
        if (messages.length) { showMessages(messages); return; }
        setBanner(null);
        if (!window.confirm(`Save voucher for ${fmt3(form.amount)}?`)) return;

        setSaving(true);
        const body = toSavePayload(form, mode, header?.financialYear || fy);
        const res = isCreate ? await api.createVoucher(body) : await api.updateVoucher(voucherNo, body);
        setSaving(false);

        if (res.ok) {
            const savedNo = res.result?.voucherNo || voucherNo;
            toast.success(`Successfully saved. Voucher No: ${savedNo}`);
            setDirty(false);
            navigate(SALE_VOUCHER_BASE, { state: { highlightVoucherNo: savedNo, refresh: Date.now() } });
        } else if (res.status === 409) {
            setConflict(true); // approved elsewhere while this screen was open; never retry the PUT
        } else if (res.status === 400) {
            setBanner(res.message); // keep the grid and the user's input
        } else {
            toast.error(res.message);
        }
    };

    const handleReload = async () => {
        const res = await api.getVoucherByNo(voucherNo, header?.financialYear || financialYear);
        setConflict(false);
        if (!res.ok) { toast.error(res.message); return; }
        setHeader({ ...res.result.header, canEdit: false });
        setForm(recomputeGrid(mapDetailToForm(res.result), taxCodesRef.current));
        setBanner(res.result.header.editBlockReason || 'Voucher Posted. Cannot Edit.');
        setDirty(false);
    };

    /* ---------------- render ---------------- */

    const allowed = isCreate ? permissions?.canAdd === true : isEdit ? permissions?.canEdit === true : permissions?.canView === true;
    if (!allowed) return <div className="p-3"><NoAccess /></div>;

    if (loadState === 'loading') return <div className="text-center p-5"><ClipLoader color="#6c5ce7" size={36} /></div>;
    if (loadState === 'error') {
        return (
            <div className="p-3">
                <div className="alert alert-danger">{loadError}</div>
                <button type="button" className="mainbtnSecondary" onClick={() => navigate(SALE_VOUCHER_BASE)}>
                    <FaAngleLeft className="center mt-1 mx-2" /><div>Back</div>
                </button>
            </div>
        );
    }
    if (isEdit && header && !header.canEdit && !banner) {
        return (
            <div className="p-3">
                <div className="alert alert-warning">{header.editBlockReason || 'Voucher Posted. Cannot Edit.'}</div>
                <button type="button" className="mainbtnSecondary" onClick={() => navigate(SALE_VOUCHER_BASE)}>
                    <FaAngleLeft className="center mt-1 mx-2" /><div>Back</div>
                </button>
            </div>
        );
    }

    const title = isCreate ? 'New Sale Voucher' : `${isEdit ? 'Edit' : 'View'} Sale Voucher ${header?.voucherNo || voucherNo || ''}`;

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center border-box2 p-3" style={{ borderRadius: '8px' }}>
                <h6 className="h6 mb-0">{title}</h6>
                <div className="d-flex justify-content-end gap-1">
                    {!locked && (
                        <button type="button" className="mainbtn" disabled={!canSave} onClick={handleSave}>
                            <FaSave className="center mt-1 mx-2" /><div>Save</div>
                        </button>
                    )}
                    <button type="button" className="mainbtnSecondary" disabled={saving} onClick={handleCancel}>
                        <FaAngleLeft className="center mt-1 mx-2" /><div>{locked ? 'Back' : 'Cancel'}</div>
                    </button>
                </div>
            </div>

            <HeaderPanel
                form={form} update={update} voucherNo={header?.voucherNo || voucherNo} mode={mode} disabled={locked || saving}
                errors={{}} banner={banner} voucherTypes={lookups.voucherTypes} currencies={lookups.currencies}
                taxCodes={lookups.taxCodes} settings={lookups.settings} amountReceived={header?.amountReceived || 0}
                onCurrency={handleCurrency} onCostCentre={handleCostCentre} onCopyCC={handleCopyCC}
                onSetFromLines={handleSetFromLines} onApplyTax={handleApplyTax}
                partyPanel={(
                    <PartyPanel
                        form={form} disabled={locked || saving} partyOptions={partyOptions} partyLoading={partyLoading}
                        detail={detail} detailLoading={detailLoading} detailMessage={detailMessage} warning={partyWarning}
                        onPartyType={(partyType) => resetPartySelection({ partyType })}
                        onUseCoa={(useCoa) => resetPartySelection({ useCoa })}
                        onCustomer={handleCustomer}
                    />
                )}
            />

            <LinesGrid
                form={form} accounts={lookups.accounts} taxCodes={lookups.taxCodes} settings={lookups.settings}
                disabled={locked || saving} totals={totals} onLine={handleLine} onAdd={handleAdd} onRemove={handleRemove}
            />

            <Modal show={conflict} backdrop="static" keyboard={false} size="sm" centered>
                <Modal.Header><Modal.Title>Voucher approved</Modal.Title></Modal.Header>
                <Modal.Body>This voucher has been approved and can no longer be edited.</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtn" onClick={handleReload}><div>Reload</div></button>
                </Modal.Footer>
            </Modal>

            {saving && (
                <div
                    role="alert" aria-busy="true"
                    style={{
                        position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.55)', color: '#fff',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
                    }}
                >
                    <ClipLoader color="#ffffff" size={48} />
                    <div>Saving voucher, please wait…</div>
                </div>
            )}
        </div>
    );
};

export default SaleVoucherRAForm;
